import { AI_ACTION_TYPES, IdSchema, IsoDateTimeSchema, type AIActionType } from '@aibi/contracts';
import { z } from 'zod';
import type { AIContext } from '../context/ai-context.js';
import { AIContextSchema } from '../context/ai-context-schema.js';
import type { AIProviderRequest } from '../provider.js';

/**
 * Protocolo de transporte navegador <-> servidor para proveedores de IA alojados en el
 * servidor (p. ej. n8n). Versionado y separado de `@aibi/contracts`: no es un contrato de
 * dominio y puede evolucionar sin regenerar los JSON Schema publicos.
 */
export const AI_RELAY_PROTOCOL_VERSION = 1;

export const AI_RELAY_ROUTES = {
  status: '/api/ai/status',
  generate: '/api/ai/generate',
} as const;

/** Proveedores que el servidor puede exponer por el relay. */
export const AI_RELAY_PROVIDERS = ['unconfigured', 'n8n', 'azure-ai'] as const;
export type AIRelayProvider = (typeof AI_RELAY_PROVIDERS)[number];

/**
 * `configured`: hay configuracion valida pero aun no hubo una llamada real.
 * `ok` / `failing`: resultado de la ultima llamada real al proveedor.
 */
export const AI_RELAY_STATES = ['unconfigured', 'configured', 'ok', 'failing'] as const;
export type AIRelayState = (typeof AI_RELAY_STATES)[number];

const RequestIdSchema = z
  .string()
  .min(1)
  .max(128)
  .regex(/^[A-Za-z0-9_.:-]+$/, 'Solo letras, digitos y los caracteres _ . : -');

/** Longitud maxima del mensaje del usuario aceptada por el relay. */
export const MAX_RELAY_MESSAGE_LENGTH = 2_000;
/** Longitud maxima del contenido devuelto por el relay (caracteres). */
export const MAX_RELAY_CONTENT_LENGTH = 64_000;

/**
 * Peticion del navegador al relay. Lista blanca estricta: no admite URLs, cabeceras,
 * credenciales, instrucciones de sistema ni JSON Schema; el servidor los construye.
 */
export const AIRelayRequestSchema = z.strictObject({
  version: z.literal(AI_RELAY_PROTOCOL_VERSION),
  requestId: RequestIdSchema,
  locale: AIContextSchema.shape.locale,
  message: z.string().min(1).max(MAX_RELAY_MESSAGE_LENGTH),
  context: AIContextSchema,
  allowedActions: z.array(z.enum(AI_ACTION_TYPES)).min(1).max(AI_ACTION_TYPES.length),
});

export interface AIRelayRequest {
  readonly version: typeof AI_RELAY_PROTOCOL_VERSION;
  readonly requestId: string;
  readonly locale: string;
  readonly message: string;
  readonly context: AIContext;
  readonly allowedActions: readonly AIActionType[];
}

/** Respuesta del relay: el texto del proveedor ya pre-validado por el servidor. */
export const AIRelayResponseSchema = z.strictObject({
  version: z.literal(AI_RELAY_PROTOCOL_VERSION),
  requestId: RequestIdSchema,
  providerId: IdSchema,
  content: z.string().min(1).max(MAX_RELAY_CONTENT_LENGTH),
});
export type AIRelayResponse = z.infer<typeof AIRelayResponseSchema>;

export const AIRelayStatusSchema = z.strictObject({
  version: z.literal(AI_RELAY_PROTOCOL_VERSION),
  provider: z.enum(AI_RELAY_PROVIDERS),
  state: z.enum(AI_RELAY_STATES),
  /** Momento de la ultima llamada real; `null` si aun no hubo ninguna. */
  checkedAt: IsoDateTimeSchema.nullable(),
});
export type AIRelayStatus = z.infer<typeof AIRelayStatusSchema>;

/**
 * Sobre que debe devolver el workflow de n8n. Estricto: cualquier propiedad adicional
 * se rechaza. `output` es el JSON del protocolo `AIModelOutput` (objeto o texto JSON).
 */
export const N8nWebhookResponseSchema = z.strictObject({
  output: z.union([
    z.string().min(1).max(MAX_RELAY_CONTENT_LENGTH),
    z.record(z.string(), z.unknown()),
  ]),
});

/**
 * Valida una peticion del relay y la devuelve tipada.
 *
 * @example
 * const parsed = parseAIRelayRequest(body);
 * if (!parsed.success) return badRequest(parsed.issues);
 */
export function parseAIRelayRequest(
  value: unknown,
):
  | { readonly success: true; readonly data: AIRelayRequest }
  | { readonly success: false; readonly issues: readonly z.core.$ZodIssue[] } {
  const parsed = AIRelayRequestSchema.safeParse(value);
  // Misma forma que `AIRelayRequest`; zod tipa los opcionales como `T | undefined`.
  return parsed.success
    ? { success: true, data: parsed.data as AIRelayRequest }
    : { success: false, issues: parsed.error.issues };
}

/**
 * Proyecta una peticion de proveedor al protocolo del relay. Descarta las instrucciones
 * de sistema, el JSON Schema y el contexto serializado: el servidor los reconstruye.
 *
 * @example
 * const body = toAIRelayRequest(providerRequest);
 */
export const toAIRelayRequest = (request: AIProviderRequest): AIRelayRequest => ({
  version: AI_RELAY_PROTOCOL_VERSION,
  requestId: request.requestId,
  locale: request.locale,
  message: request.userMessage,
  context: request.context,
  allowedActions: request.allowedActions,
});
